import { useCallback, useEffect, useMemo, useState } from "react";
import { Checkbox, ClearableInput, Select, SimpleTooltip, Spinner } from "@rio-cloud/rio-uikit";

import { Asset, AssetGroup } from "../../models/asset";

import { buildAssetById, buildAssetTypeById, filterSelectedAssetIdsBySearch, getAssetGroups, getAssetsByGroup, getFilteredAssetIds, getVehicleTypeCounts, IAssetGroup } from "../../services/memoryAssetService";

import UptimeTagRow from "./components/UptimeTagRow";

import "./styles.scss";

interface UptimeSidebarProps {
	allAssets: Asset[];
	allGroups: AssetGroup[];

	setAssetIds: (assetIds: string[]) => void;
}

const LIMIT = 20;

const UptimeSidebar = ({
	allAssets,
	allGroups,

	setAssetIds,
}: UptimeSidebarProps) => {

	const [viewMode, setViewMode] = useState<"assets" | "groups">("assets");
	const [searchTerm, setSearchTerm] = useState('');
	const [offset, setOffset] = useState(0);

	const [selectedAllAssets, setSelectedAllAssets] = useState(true);

	const [groups, setGroups] = useState<IAssetGroup[]>([]);
	const [hasMoreGroups, setHasMoreGroups] = useState(false);
	const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(new Set());

	const publishSelection = useCallback((assetIds: Set<string>) => {
		setAssetIds(Array.from(assetIds));
	}, [setAssetIds]);

	const clearSelection = useCallback(() => {
		setSelectedAllAssets(false);
		const emptySelection = new Set<string>();
		setSelectedAssetIds(emptySelection);
		publishSelection(emptySelection);
	}, [publishSelection]);

	const handleTagAssetsSelect = useCallback((tagId: string, tagSelectedAssetIds: string[]) => {
		setSelectedAllAssets(false);

		const tagAssetIdSet = new Set(
			getAssetsByGroup(allAssets, tagId, searchTerm, viewMode).map((asset) => asset.id)
		);

		setSelectedAssetIds((previousSelection) => {
			const nextSelection = new Set(
				[...previousSelection].filter((assetId) => !tagAssetIdSet.has(assetId))
			);
			tagSelectedAssetIds.forEach((assetId) => nextSelection.add(assetId));
			publishSelection(nextSelection);
			return nextSelection;
		});
	}, [allAssets, publishSelection, searchTerm, viewMode]);

	const handleSearchTermChange = (term: string) => {
		setOffset(0);
		setSearchTerm(term);
		clearSelection();
	};

	const handleViewModeChange = (mode: "assets" | "groups") => {
		setOffset(0);
		setViewMode(mode);
	};

	useEffect(() => {
		setOffset(0);
	}, [searchTerm, viewMode]);

	useEffect(() => {
		const fetchedGroups = getAssetGroups(allAssets, allGroups, searchTerm, viewMode, LIMIT, offset);
		setGroups((previousGroups) =>
			offset === 0 ? fetchedGroups.groups : [...previousGroups, ...fetchedGroups.groups]
		);
		setHasMoreGroups(fetchedGroups.hasMore);
	}, [allAssets, allGroups, searchTerm, offset, viewMode]);

	useEffect(() => {
		if (!selectedAllAssets || allAssets.length === 0) {
			return;
		}

		const allSelectedAssetIds = new Set(
			getFilteredAssetIds(allAssets, allGroups, searchTerm, viewMode)
		);
		setSelectedAssetIds(allSelectedAssetIds);
		publishSelection(allSelectedAssetIds);
	}, [selectedAllAssets, allAssets, allGroups, searchTerm, viewMode, publishSelection]);

	const filteredAssetIds = useMemo(
		() => new Set(getFilteredAssetIds(allAssets, allGroups, searchTerm, viewMode)),
		[allAssets, allGroups, searchTerm, viewMode]
	);

	const filteredSelectionState = useMemo(() => {
		let selectedFilteredCount = 0;

		for (const assetId of filteredAssetIds) {
			if (selectedAssetIds.has(assetId)) {
				selectedFilteredCount++;
			}
		}

		const filteredCount = filteredAssetIds.size;

		return {
			areAllFilteredAssetsSelected: filteredCount > 0 && selectedFilteredCount === filteredCount,
			areSomeFilteredAssetsSelected: selectedFilteredCount > 0 && selectedFilteredCount < filteredCount,
		};
	}, [filteredAssetIds, selectedAssetIds]);

	const handleToggleSelectAll = () => {
		if (!filteredSelectionState.areAllFilteredAssetsSelected) {
			const filteredSelection = new Set(filteredAssetIds);
			setSelectedAllAssets(!searchTerm && filteredSelection.size === allAssets.length);
			setSelectedAssetIds(filteredSelection);
			publishSelection(filteredSelection);
			return;
		}

		setSelectedAllAssets(false);
		setSelectedAssetIds((previousSelection) => {
			const nextSelection = new Set(
				[...previousSelection].filter((assetId) => !filteredAssetIds.has(assetId))
			);
			publishSelection(nextSelection);
			return nextSelection;
		});
	};

	const getSearchLabel = () => {
		switch (viewMode) {
			case "assets":
				return "Buscar veículos";
			case "groups":
				return "Buscar grupos";
		}
	}

	const assetById = useMemo(() => buildAssetById(allAssets), [allAssets]);
	const assetTypeById = useMemo(() => buildAssetTypeById(allAssets), [allAssets]);

	const filteredSelectedAssetIds = useMemo(
		() => filterSelectedAssetIdsBySearch(assetById, allGroups, selectedAssetIds, searchTerm, viewMode),
		[assetById, allGroups, selectedAssetIds, searchTerm, viewMode]
	);

	const vehicleTypeCounts = useMemo(
		() => getVehicleTypeCounts(assetTypeById, filteredSelectedAssetIds),
		[assetTypeById, filteredSelectedAssetIds]
	);

	if (!allAssets?.length || !allGroups?.length) {
		return (
			<div className="height-100pct display-flex align-items-center justify-content-center">
				<Spinner />
			</div>
		);
	}

	return (
		<div className="Tree" style={{ height: "100%" }}>
			<div className="TreeHeader">
				<div className="TreeSearch width-100pct">
					<div className="input-group width-100pct">
						<div className="input-group width-100pct">
							<span className="input-group-addon">
								<span className="rioglyph rioglyph-search" aria-hidden="true" />
							</span>{" "}
							<ClearableInput placeholder={getSearchLabel()} onChange={handleSearchTermChange} />
							<SimpleTooltip content={"Tipo de busca"}>
								<span className="input-group-btn">
									<Select
										pullRight={true}
										showSelectedItemIcon={true}
										dropdownClassName="width-auto"
										onChange={(item) => handleViewModeChange(item?.short! as "assets" | "groups")}
										options={[
											{
												id: "1",
												label: "Veículos",
												short: "assets",
												icon: <span className="rioglyph text-size-16 rioglyph-status-driving text-color-primary" />,
												selected: viewMode == "assets",
											},
											{
												id: "2",
												label: "Grupos",
												short: "groups",
												icon: <span className="rioglyph text-size-16 rioglyph-tag text-color-primary" />,
												selected: viewMode == "groups",
											},
										]}
										placeholder="Tipo de busca"
									/>
								</span>
							</SimpleTooltip>
						</div>
					</div>
				</div>

				<div className="TreeHead display-flex gap-5 padding-15">
					<div className="border border-right-only hidden-empty padding-right-10 margin-right-2">
						<div className="TreeSelectAll display-flex align-items-center">
							<label className="checkbox margin-top--1" id="checkbox-all">
								<Checkbox
									indeterminate={filteredSelectionState.areSomeFilteredAssetsSelected}
									checked={filteredSelectionState.areAllFilteredAssetsSelected}
									onChange={handleToggleSelectAll}
								/>
							</label>
						</div>
					</div>
					<div className="display-flex justify-content-between align-items-start width-100pct">
						<div className="TreeSummary display-flex flex-wrap align-items-center gap-10 padding-left-3">
							{Object.entries(vehicleTypeCounts).map(([type, count]) => (
								<div key={type} className="TypeCounter display-flex align-items-center user-select-none margin-right-2 cursor-pointer">
									<span className={`rioglyph rioglyph-${type} text-size-16 margin-right-2`}></span>
									<span className="TreeLabelCount label label-condensed label-muted label-filled">{count}</span>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
			<div
				className="TreeRoot user-select-none"
				style={{ height: `calc(100% - 100px)`, overflow: "auto" }}
			>
				{groups
					.map((tag: IAssetGroup) => <UptimeTagRow
						key={tag.id}
						allAssets={allAssets}
						tagId={tag.id}
						tagName={tag.name}
						assetsCount={tag.assetsCount}
						searchTerm={searchTerm}
						viewMode={viewMode}
						selectedAssetIds={selectedAssetIds}
						onAssetsSelect={(assetIds) => handleTagAssetsSelect(tag.id, assetIds)}
					/>)}

				{hasMoreGroups && (
					<div
						className="LoadMore cursor-pointer text-center py-8 text-color-primary"
						onClick={() => setOffset(offset + LIMIT)}
					>
						Carregar mais
					</div>
				)}
			</div>
		</div>
	);
};

export default UptimeSidebar;
